/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Portal-rendered action and filter dropdowns for ViewAppointments.
 * @module pages/viewAppointments/ViewAppointmentsDropdownPortals
 */

import ReactDOM from "react-dom";
import Select from "react-select";
import {
  getStatusOptions,
  getRequirementsOptions,
} from "../../data/selectOptions";
import styles from "../../styles/ViewAppointments.module.css";
import { COMMON_SELECT_PLACEHOLDER_KEY } from "./viewAppointmentsConstants.js";
import {
  formatSelectOptionLabel,
  getSelectOptionValue,
} from "./viewAppointmentsSelectUtils.jsx";

const ViewAppointmentsDropdownPortals = ({
  t,
  activeDropdownId,
  dropdownDirection,
  dropdownPosition,
  isFiltersDropdownOpen,
  filtersDropdownDirection,
  filtersDropdownPosition,
  selectedStatusFilter,
  selectedRequirementsFilter,
  filterDropdownSelectStyles,
  onActiveDropdownSeeProfile,
  onActiveDropdownSeeRequest,
  onActiveDropdownMakeVirtual,
  isActiveDropdownMakeVirtualDisabled,
  onClearFilters,
  onStatusFilterChange,
  onRequirementsFilterChange,
}) => (
  <>
    {activeDropdownId &&
      ReactDOM.createPortal(
        <div
          className={`${styles.actionsDropdownMenu} ${
            dropdownDirection === "above"
              ? styles.dropdownAbove
              : styles.dropdownBelow
          }`}
          style={{
            position: "fixed",
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            zIndex: 999999,
          }}
        >
          <button
            className={styles.actionsDropdownItem}
            onClick={onActiveDropdownSeeProfile}
          >
            {t("pages.viewAppointments.seeProfile")}
          </button>
          <button
            className={styles.actionsDropdownItem}
            onClick={onActiveDropdownSeeRequest}
          >
            {t("pages.viewAppointments.seeRequest")}
          </button>
          <button
            className={styles.actionsDropdownItem}
            disabled={isActiveDropdownMakeVirtualDisabled()}
            onClick={onActiveDropdownMakeVirtual}
          >
            {t("pages.viewAppointments.makeVirtual")}
          </button>
        </div>,
        document.body,
      )}

    {isFiltersDropdownOpen &&
      ReactDOM.createPortal(
        <div
          className={`${styles.filtersDropdownMenu} ${
            filtersDropdownDirection === "above"
              ? styles.dropdownAbove
              : styles.dropdownBelow
          }`}
          style={{
            position: "fixed",
            top: filtersDropdownPosition.top,
            left: filtersDropdownPosition.left,
            zIndex: 999999,
          }}
        >
          <div className={styles.filtersDropdownContent}>
            <div className={styles.filtersHeader}>
              <div className={styles.filtersTitle}>
                {t("pages.viewAppointments.filters")}
              </div>
              <button
                className={styles.clearFiltersButton}
                onClick={onClearFilters}
              >
                {t("pages.viewAppointments.clearFilters")}
              </button>
            </div>

            <div className={styles.filterSection}>
              <div className={styles.filterLabel}>
                {t("pages.viewAppointments.status")}
              </div>
              <Select
                options={getStatusOptions(t)}
                value={selectedStatusFilter}
                onChange={onStatusFilterChange}
                placeholder={t(COMMON_SELECT_PLACEHOLDER_KEY)}
                isSearchable={false}
                styles={filterDropdownSelectStyles}
                getOptionValue={getSelectOptionValue}
                formatOptionLabel={formatSelectOptionLabel}
              />
            </div>

            <div className={styles.filterSection}>
              <div className={styles.filterLabel}>
                {t("pages.viewAppointments.requirements")}
              </div>
              <Select
                options={getRequirementsOptions(t)}
                value={selectedRequirementsFilter}
                onChange={onRequirementsFilterChange}
                placeholder={t(COMMON_SELECT_PLACEHOLDER_KEY)}
                isSearchable={false}
                styles={filterDropdownSelectStyles}
                getOptionValue={getSelectOptionValue}
                formatOptionLabel={formatSelectOptionLabel}
              />
            </div>
          </div>
        </div>,
        document.body,
      )}
  </>
);

export default ViewAppointmentsDropdownPortals;
